/** Optional chess engine / review trace (?chessDebug=1 or localStorage). */

export type ChessDebugEntry = {
  id: number;
  timestamp: number;
  phase: string;
  detail?: string;
};

const MAX_ENTRIES = 120;
const STORAGE_KEY = 'codenesium.chessDebug';

const entries: ChessDebugEntry[] = [];
let nextId = 1;
let enabled = false;

function readEnabledFromUrl(): boolean {
  try {
    return new URLSearchParams(window.location.search).has('chessDebug');
  } catch {
    return false;
  }
}

export function isChessDebugEnabled(): boolean {
  return enabled;
}

export function setChessDebugEnabled(on: boolean): void {
  enabled = on;
  try {
    if (on) localStorage.setItem(STORAGE_KEY, '1');
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export function initChessDebug(): void {
  try {
    enabled = readEnabledFromUrl() || localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    enabled = readEnabledFromUrl();
  }
}

export function logChessDebug(phase: string, detail?: Record<string, unknown>): void {
  if (!enabled) return;
  const text = detail ? JSON.stringify(detail) : undefined;
  const entry: ChessDebugEntry = {
    id: nextId++,
    timestamp: Date.now(),
    phase,
    ...(text ? { detail: text } : {}),
  };
  entries.unshift(entry);
  if (entries.length > MAX_ENTRIES) entries.length = MAX_ENTRIES;
  const line = text ? `[chess] ${phase} ${text}` : `[chess] ${phase}`;
  console.info(line);
  document.dispatchEvent(new CustomEvent('chess-debug', { detail: entry }));
}

export function getChessDebugEntries(): readonly ChessDebugEntry[] {
  return entries;
}

export function formatChessDebugLogText(
  source: readonly ChessDebugEntry[] = entries,
  limit = 40
): string {
  return source
    .slice(0, limit)
    .map(e => {
      const t = new Date(e.timestamp).toISOString().slice(11, 23);
      return e.detail ? `${t} ${e.phase} ${e.detail}` : `${t} ${e.phase}`;
    })
    .join('\n');
}

export function clearChessDebugEntries(): void {
  entries.length = 0;
}
